import { motion } from "framer-motion";
import { useState } from "react";
import { Beer, ChevronDown, ChevronRight, ExternalLink, Loader2, Lock, MapPin, MessageCircle, Phone, Play, Sparkles, X } from "lucide-react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  useDismiss,
  useHover,
  useRole,
  useInteractions,
  safePolygon,
  FloatingPortal,
} from "@floating-ui/react";
import { viewportClamp } from "../lib/floating";
import { useCloseOutBarConfig, useSetCloseOutBar, useAutoAssignCloseOutBars, useToggleCloseOutChat } from "../api";
import { useToast } from "@/components/ux/Toast";
import { AdminAPI } from "@shared/types/api";

interface CloseOutBarConfigProps {
  eventTimeId: string | undefined;
}

const MIN_TABLES = 3;

function formatKm(km: number | null): string {
  if (km == null) return "—";
  if (km < 1) return `${Math.round(km * 1000)}m`;
  return `${km.toFixed(1)}km`;
}

export function CloseOutBarConfig({ eventTimeId }: CloseOutBarConfigProps) {
  const { data, isLoading } = useCloseOutBarConfig(eventTimeId);
  const setBar = useSetCloseOutBar(eventTimeId);
  const autoAssign = useAutoAssignCloseOutBars(eventTimeId);
  const toggleChat = useToggleCloseOutChat(eventTimeId);
  const toast = useToast();

  const neighborhoods: AdminAPI.CloseOutBarNeighborhood[] = [
    ...(data?.data?.neighborhoods ?? []),
  ].sort((a, b) => b.tableCount - a.tableCount);

  const eligibleCount = neighborhoods.filter((n) => n.tableCount >= MIN_TABLES).length;
  const assignedCount = neighborhoods.filter((n) => n.selectedBarRestaurantId).length;
  const [isExpanded, setIsExpanded] = useState(false);

  const handleSelect = (neighborhoodId: string, barRestaurantId: string | null) => {
    setBar.mutate(
      { neighborhoodId, barRestaurantId },
      {
        onError: (err: any) => toast.error(err?.message || "Failed to save bar"),
      }
    );
  };

  const handleAutoAssign = () => {
    autoAssign.mutate(undefined, {
      onSuccess: (resp) => {
        const assigned = resp?.data?.assigned.length ?? 0;
        const skipped = resp?.data?.skipped.length ?? 0;
        toast.success(
          `Auto-assigned ${assigned} neighborhood(s)${skipped ? `, skipped ${skipped}` : ""}`
        );
      },
      onError: (err: any) => toast.error(err?.message || "Failed to auto-assign"),
    });
  };

  const handleToggleChat = (neighborhoodId: string, action: "open" | "close") => {
    toggleChat.mutate(
      { neighborhoodId, action },
      {
        onSuccess: () => {
          toast.success(action === "open" ? "Close-out chat opened" : "Close-out chat closed");
        },
        onError: (err: any) =>
          toast.error(err?.message || `Failed to ${action} close-out chat`),
      }
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card mb-6"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="flex items-center gap-3 text-left flex-1 min-w-0"
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-base-content/60 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-base-content/60 shrink-0" />
          )}
          <Beer className="w-5 h-5 text-primary-500 shrink-0" />
          <div className="min-w-0">
            <h2 className="text-lg font-semibold flex items-center gap-2 flex-wrap">
              Close-Out Bars
              <span className="text-xs font-normal text-base-content/60">
                {assignedCount} / {neighborhoods.length} assigned
              </span>
            </h2>
            {isExpanded && (
              <p className="text-xs text-base-content/60">
                Pick the after-dinner bar for each neighborhood. Auto-assign uses the
                closest bar (avg distance to assigned restaurants) for neighborhoods
                with at least {MIN_TABLES} tables.
              </p>
            )}
          </div>
        </button>
        {isExpanded && (
          <button
            onClick={handleAutoAssign}
            disabled={autoAssign.isPending || eligibleCount === 0}
            className="btn btn-primary btn-sm"
            title={
              eligibleCount === 0
                ? `No neighborhoods with ${MIN_TABLES}+ tables`
                : `Auto-assign ${eligibleCount} neighborhood(s)`
            }
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {autoAssign.isPending ? "Auto-assigning..." : "Auto-assign"}
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-6">
              <div className="loading loading-spinner loading-md text-primary"></div>
            </div>
          ) : neighborhoods.length === 0 ? (
            <p className="text-sm text-base-content/60 py-4 text-center">
              No neighborhoods have tables for this event yet.
            </p>
          ) : (
            <div className="border border-base-300 rounded-lg divide-y divide-base-300">
              {neighborhoods.map((n) => (
                <CloseOutBarNeighborhoodRow
                  key={n.neighborhoodId}
                  neighborhood={n}
                  onSelect={handleSelect}
                  onToggleChat={handleToggleChat}
                  isSaving={setBar.isPending}
                  isTogglingChat={toggleChat.isPending}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

interface RowProps {
  neighborhood: AdminAPI.CloseOutBarNeighborhood;
  onSelect: (neighborhoodId: string, barRestaurantId: string | null) => void;
  onToggleChat: (neighborhoodId: string, action: "open" | "close") => void;
  isSaving: boolean;
  isTogglingChat: boolean;
}

function CloseOutBarNeighborhoodRow({
  neighborhood,
  onSelect,
  onToggleChat,
  isSaving,
  isTogglingChat,
}: RowProps) {
  const {
    neighborhoodId,
    neighborhoodName,
    cityName,
    tableCount,
    selectedBarRestaurantId,
    candidates,
    chatState,
    conversationId,
  } = neighborhood;

  const eligible = tableCount >= MIN_TABLES;
  const selected = candidates.find((c) => c.barRestaurantId === selectedBarRestaurantId);

  return (
    <div className="flex items-center gap-2 py-1.5 px-2 border-b border-base-300 last:border-0 flex-wrap">
      <div className="flex items-center gap-1.5 min-w-0 w-44 shrink-0">
        <MapPin className="w-3.5 h-3.5 text-primary-500 shrink-0" />
        <span className="text-xs capitalize truncate" title={`${cityName ? `${cityName} — ` : ""}${neighborhoodName}`}>
          {cityName ? `${cityName} — ` : ""}{neighborhoodName}
        </span>
        <span className="text-[10px] text-base-content/60 shrink-0">
          {tableCount}t{!eligible && <span className="text-amber-500">*</span>}
        </span>
      </div>

      {candidates.length === 0 ? (
        <span className="text-[10px] text-base-content/60 italic">
          No bars (add restaurant with type="bar")
        </span>
      ) : (
        <div className="flex flex-wrap gap-1 flex-1 min-w-0">
          {candidates.map((c) => {
            const isSelected = c.barRestaurantId === selectedBarRestaurantId;
            return (
              <CloseOutBarChip
                key={c.barRestaurantId}
                candidate={c}
                isSelected={isSelected}
                isSaving={isSaving}
                onToggle={() =>
                  onSelect(neighborhoodId, isSelected ? null : c.barRestaurantId)
                }
              />
            );
          })}
        </div>
      )}

      {selected && (
        <div className="flex items-center gap-1 shrink-0">
          {chatState === "open" ? (
            <>
              {conversationId && (
                <a
                  href={`/admin/chats/?chat=${encodeURIComponent(conversationId)}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-primary-500/40 text-primary-500 hover:bg-primary-500/10"
                  title="Open the close-out chat in the admin chats view"
                >
                  <MessageCircle className="w-3 h-3" />
                  Chat
                </a>
              )}
              <button
                onClick={() => onToggleChat(neighborhoodId, "close")}
                disabled={isTogglingChat}
                className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-amber-500/40 text-amber-600 hover:bg-amber-500/10 disabled:opacity-50"
                title="Hide the close-out chat from user cards and prevent the automation from re-opening it"
              >
                {isTogglingChat ? <Loader2 className="w-3 h-3 animate-spin" /> : <Lock className="w-3 h-3" />}
                Close chat
              </button>
            </>
          ) : (
            <button
              onClick={() => onToggleChat(neighborhoodId, "open")}
              disabled={isTogglingChat}
              className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border border-primary-500/40 text-primary-500 hover:bg-primary-500/10 disabled:opacity-50"
              title={
                chatState === "closed"
                  ? "Re-open the close-out chat (creates a new conversation)"
                  : "Open the close-out chat now instead of waiting for the automation"
              }
            >
              {isTogglingChat ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              {chatState === "closed" ? "Re-open chat" : "Open chat now"}
            </button>
          )}
          <button
            onClick={() => onSelect(neighborhoodId, null)}
            disabled={isSaving}
            className="text-base-content/60 hover:text-base-content"
            title="Clear selection"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
}

interface CloseOutBarChipProps {
  candidate: AdminAPI.CloseOutBarCandidate;
  isSelected: boolean;
  isSaving: boolean;
  onToggle: () => void;
}

function CloseOutBarChip({ candidate, isSelected, isSaving, onToggle }: CloseOutBarChipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(6), flip(), ...viewportClamp(320)],
    whileElementsMounted: autoUpdate,
    placement: "top",
  });
  const hover = useHover(context, {
    delay: { open: 150, close: 100 },
    move: false,
    handleClose: safePolygon(),
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss, role]);

  const hasDetails =
    !!candidate.barAddress ||
    !!candidate.barWebsite ||
    !!candidate.barGoogleMapsLink ||
    !!candidate.barPhone;

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={onToggle}
        disabled={isSaving}
        className={`px-2 py-0.5 rounded border text-[11px] transition-colors flex items-center gap-1.5 ${
          isSelected
            ? "border-primary-500 bg-primary/10 text-primary-500 font-medium"
            : "border-base-300 hover:border-primary-500/50 hover:bg-base-200"
        }`}
      >
        <span className="truncate max-w-[140px]">{candidate.barName}</span>
        <span className="text-[10px] text-base-content/60 shrink-0">
          {formatKm(candidate.avgDistanceKm)}/{formatKm(candidate.maxDistanceKm)}
        </span>
      </button>
      {isOpen && hasDetails && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[9999] bg-dark-surface border border-dark-border rounded-lg shadow-2xl max-w-xs p-3 text-xs"
          >
            <div className="font-semibold text-sm mb-1">{candidate.barName}</div>
            {candidate.barAddress && (
              <div className="flex items-start gap-1.5 text-dark-muted mb-2">
                <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{candidate.barAddress}</span>
              </div>
            )}
            <div className="text-dark-muted mb-2">
              avg {formatKm(candidate.avgDistanceKm)} · max {formatKm(candidate.maxDistanceKm)}
            </div>
            <div className="flex flex-wrap gap-2">
              {candidate.barWebsite && (
                <a
                  href={candidate.barWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-primary-500 hover:text-primary-600"
                >
                  <ExternalLink className="w-3 h-3" />
                  Website
                </a>
              )}
              {candidate.barGoogleMapsLink && (
                <a
                  href={candidate.barGoogleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-primary-500 hover:text-primary-600"
                >
                  <MapPin className="w-3 h-3" />
                  Google Maps
                </a>
              )}
              {candidate.barPhone && (
                <a
                  href={`tel:${candidate.barPhone}`}
                  onClick={(e) => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-primary-500 hover:text-primary-600"
                >
                  <Phone className="w-3 h-3" />
                  {candidate.barPhone}
                </a>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
